import { useCallback } from 'react';
import { atom, useRecoilState } from 'recoil';
import { localStorageEffect } from 'libs/recoil';

type SearchDrawerState = {
  open: boolean;
  maximized: boolean;
  sortDescending: boolean;
};

type UseSearchDrawerState = {
  open: boolean;
  maximized: boolean;
  sortDescending: boolean;
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  setMaximized: (maximized: boolean) => void;
  toggleSortDescending: () => void;
};

const DEFAULT_SEARCH_DRAWER_STATE: SearchDrawerState = {
  open: true,
  maximized: false,
  sortDescending: true,
};

const searchDrawerStateAtom = atom({
  key: 'SEARCH_DRAWER_STATE',
  default: DEFAULT_SEARCH_DRAWER_STATE,
  effects: [
    localStorageEffect<SearchDrawerState>(
      'SEARCH_DRAWER_STATE',
      (savedValue) => ({ ...DEFAULT_SEARCH_DRAWER_STATE, ...savedValue }),
    ),
  ],
});

export const useSearchDrawerState = (): UseSearchDrawerState => {
  const [searchDrawerState, setSearchDrawerState] = useRecoilState(
    searchDrawerStateAtom,
  );

  const toggleOpen = useCallback(() => {
    setSearchDrawerState((prevState) => ({
      ...prevState,
      open: !prevState.open,
    }));
  }, [setSearchDrawerState]);

  const setOpen = useCallback(
    (open: boolean) => {
      setSearchDrawerState((prevState) => ({ ...prevState, open }));
    },
    [setSearchDrawerState],
  );

  const setMaximized = useCallback(
    (maximized: boolean) => {
      setSearchDrawerState((prevState) => ({ ...prevState, maximized }));
    },
    [setSearchDrawerState],
  );

  const toggleSortDescending = useCallback(() => {
    setSearchDrawerState((prevState) => ({
      ...prevState,
      sortDescending: !prevState.sortDescending,
    }));
  }, [setSearchDrawerState]);

  return {
    open: searchDrawerState.open,
    maximized: searchDrawerState.maximized,
    sortDescending: searchDrawerState.sortDescending,
    toggleOpen,
    setOpen,
    setMaximized,
    toggleSortDescending,
  };
};
